import { useRouter } from "easy-page-router/react";
import { Lang } from "../../types";

type LangDict = {
    lang: Lang,
    dict: Dictionary,
}

export type Dictionary = typeof csDict;

export const languages: Lang[] = ["en", "cs", "es"];

export function useLang(): LangDict {
    const { path } = useRouter();
    return getLang(path);
}

export function getLang(path: string[]): LangDict {
    let lang: Lang = "en";

    const csUrls = ["admin", ...urlKeys.map(key => csDict[key])];
    const esUrls = urlKeys.map(key => esDict[key]);

    if (csUrls.includes(path[0])) {
        lang = "cs";
    } else if (esUrls.includes(path[0])) {
        lang = "es";
    }

    return { lang, dict: dictionary[lang] };
}

export function plural(words: string[], count: number) {
    const index = Math.floor(count) - 1;

    if (words[index]) {
        return words[index];
    } else {
        return words[words.length - 1];
    }
}

export const csDict = {
    url: "cs",
    urlDonate: "kup-kafe",
    urlForgottenPassword: "zapomenute-heslo",
    urlCountdown: "odpocet",
    urlTimer: "casovac",
    urlPoll: "hlasovani",
    urlWho: "kdo",
    urlWhen: "kdy",
    urlTeam: "tym",
    urlNew: "novy",

    wizard: {
        cta: "Chci vytvořit... ✨",
        title: "Co byste rádi vytvořili?",
        subTitle: "Vyberte si podle toho, co dnes plánujete",
        countdownTitle: "Odpočet času",
        countdownDesc: "Chci odpočítávat čas do události (narozeniny, dovolená, spuštění projektu...)",
        whenTitle: "Společný termín (Kdy?)",
        whenDesc: "Potřebuji najít termín schůzky nebo oslavy podle toho, kdy má většina lidí čas.",
        whoTitle: "Účast a souhlas (Kdo?)",
        whoDesc: "Chci zjistit, kdo dorazí na akci nebo kdo souhlasí s navrženým rozhodnutím.",
        pollTitle: "Hlasování a anketa (Co?)",
        pollDesc: "Potřebuji nechat lidi hlasovat o vlastních možnostech (výběr jídla, filmu, dárku...).",
        teamTitle: "Tým a docházka (Tým!)",
        teamDesc: "Organizuji pravidelné akce (tréninky, zkoušky kapely) a chci sledovat docházku.",
    },

    // general
    localDataInfo: "Tento seznam je uložen ve vašem prohlížeči.",
    save: "Uložit",
    change: "Změnit",
    cancel: "Zrušit",
    close: "Zavřít",

    yes: "Ano",
    no: "Ne",
    maybe: "Možná",

    firstDayOfWeek: 1, // 0 - sunday, 1 - monday (standard pro ČR)
    daysOfWeek: ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"], // Plné názvy dnů
    daysOfWeekShort: ["ne", "po", "út", "st", "čt", "pá", "so"], // Krátké názvy dnů
    month: ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"], // Měsíce (nominativ)
    monthCalendar: ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"], // Měsíce (genitiv - pro datumy jako "5. ledna")

    getNumberVoters: (count: number) => count === 0
        ? <>Zatím <b>nikdo</b> nehlasoval.</>
        : count === 1 ? <>Hlasoval <b>1</b> uživatel.</>
            : count <= 4 ? <>Hlasovali <b>{count}</b> uživatelé.</>
                : <>Hlasovalo <b>{count}</b> uživatelů.</>,

    errorBoundary: {
        title: "Jejda, tady se něco pokazilo",
        apology: "Omlouváme se za komplikace. Aplikace narazila na nečekaný problém.",
        action: ["Zkuste prosím", "obnovit stránku", "nebo se vrátit", "zpět na domovskou stránku", "."],
        info: "Pokud problém přetrvává, chyba byla pravděpodobně automaticky zaznamenána a my už o ní víme. Brzy to opravíme!",
    },

    // user
    userNameUnknown: "Neznámý uživatel",
    userNameMissing: "Prosím, vyplň jméno (nebo přezdívku) 🧐",
    userNamePlaceholder: "Tvoje jméno (nebo přezdívka)...",
    userMonogram: "Iniciály",
    userEmoji: "Emoji",
    userBackground: "Barva pozadí",
    userMonogramPlaceholder: "Napiš vlastní iniciály nebo emoji...",
    userDialogLoggedInHeader: "Jsi přihlášen jako:",
    userCreateAccount: "Vytvořit účet",
    userDialogText: "Účet je tu hlavně proto, abys mohl používat titimeme.com pohodlně na více zařízeních.",

    userAdmin: "Správce",
    userNotMember: "Není člen",

    mail: "E-mail",
    password: "Heslo",
    passwordAgain: "Heslo znovu",
    userWrongPasswordMatch: "Zadaná hesla se neshodují 🧐",
    userWrongPasswordAgain: "Zadej heslo znovu",
    userWrongPasswordLength: "Heslo musí mít alespoň 6 znaků",
    userWrongMailFormat: "E-mail je ve špatném formátu 🧐",
    userWrongMailEmpty: "Vyplň prosím e-mail",
    userWrongFill: "Vyplň prosím přihlašovací údaje",
    userErrorLogIn: "Přihlašovací údaje nejsou správné 🙁",
    userErrorRegister: "Účet se bohužel nepodařilo vytvořit 🙁",
    userWrongSendMail: "Bohužel, tenhle e-mail u nás nemáme 🧐",

    userForgottenPassword: "Zapomenuté heslo",
    userForgottenPasswordButton: "Zapomněl jsem heslo 🙁",
    userForgottenPasswordSuccess: "Na tvůj e-mail jsme odeslali odkaz pro změnu hesla 😉",
    userForgottenPasswordSuccessChange: "Heslo máš úspěšně změněné. 😉",
    userForgottenPasswordError: "Bohužel, něco se pokazilo. Zkus to prosím později 🙁",
    userForgottenPasswordErrorChange: "Změna hesla se nezdařila. Zkus si prosím poslat odkaz pro změnu hesla ještě jednou 🙁",
    userForgottenPasswordErrorSend: "Odeslat odkaz pro změnu hesla",
    userForgottenPasswordChange: "Změnit heslo",

    allRight: "Vše je v pořádku.",
    logOut: "Odhlásit se",
    logIn: "Přihlásit se",
    registerIn: "Začít používat titimeme.com naplno", // Případně: "Vytvořit účet zdarma"

    // not found
    notFound: "404 - stránka nenalezena",
    backToHome: "Zpět na domovskou stránku",

    // popup
    popup: <><b>Tohle není vše!</b> 😉 Titimeme je celá sada nástrojů pro jednodušší plánování. Klikněte a prozkoumejte je, vše je zdarma!</>,

    // messenger redirect
    messengerRedirectInfo: <>Pro využití všech funkcí otevři <span className="titimeme"><span>ti</span><b>time</b><span>me</span></span> v běžném prohlížeči.</>,
    messengerRedirectButton: "Otevřít v prohlížeči",
    messengerRedirectHelp: <>Klikni na <b>⋯</b> nebo <b>🧭</b> a zvol <b>"Otevřít v prohlížeči"</b></>,

    // home screen
    countdown: "Kdy!",
    countdownSubTitle: "Odpočet času",
    countdownDescription: "Vytvořte stylový odpočet k jakékoli události a sdílejte napětí s ostatními. Ideální pro narozeniny, svátky nebo start projektu.",
    when: "Kdy?",
    whenSubTitle: "Hledání termínu",
    whenDescription: "Plánujete schůzku nebo akci a nemůžete najít společný termín? Navrhněte několik možností a nechte ostatní hlasovat Ano, Ne nebo Možná pro každé datum. Jednoduchá alternativa k Doodle.",
    who: "Kdo?",
    whoSubTitle: "Účast nebo Souhlas",
    whoDescription: "Perfektní nástroj pro rychlé zjištění účasti nebo souhlasu. Kdo přijde na večírek? Kdo souhlasí s návrhem? Hlasujte jednoduše Ano, Ne, nebo Možná.",
    where: "Kde?",
    whereSubTitle: "Lokalita",
    whereDescription: "Brzy budete moci snadno hlasovat i o nejlepší lokalitě pro vaši akci.",
    what: "Co?",
    whatSubTitle: "Hlasování a ankety",
    whatDescription: "Potřebujete zjistit názor skupiny na cokoli? Vytvořte rychlou anketu s vlastními možnostmi odpovědí. Skvělé pro výběr filmu, tématu diskuze nebo místa setkání.",
    team: "Tým!",
    teamSubTitle: "Organizace skupin",
    teamDescription: "Nástroj pro efektivní organizaci pravidelných aktivit jako jsou sportovní tréninky, divadelní zkoušky nebo jiné skupinové akce.",
    preparing: "Připravuje se...",

    home: {
        subTitle: "Snadná organizace času, událostí a hlasování online. Zdarma a bez registrace.",
        text: <>Sada <strong>moderních</strong>, <strong>intuitivních</strong> a zcela <strong>bezplatných</strong> online nástrojů navržených tak, aby vám co nejvíce usnadnily <strong>plánování a sdílení s přáteli, kolegy nebo rodinou.</strong></>,
        words: ["Jednoduchost", "Bez registrace", "Zdarma", "Bez reklam", "Snadné sdílení"],
        wordsText: [
            "Všechny nástroje jsou navrženy pro maximálně intuitivní ovládání.",
            "Žádné zdlouhavé přihlašování. Vytvořte a sdílejte během okamžiku.",
            "Používejte všechny nástroje a funkce zcela bez poplatků.",
            "Používejte všechny funkce naplno, aniž by vás rušily otravné reklamy.",
            "Každý vytvořený nástroj má unikátní odkaz pro jednoduché sdílení.",
        ],
    },

    // donate page
    donate: "Kup kafe",

    // theme
    theme: "Styl",
    themeSystem: "Dle systému",
    themeLight: "Svetlé",
    themeDark: "Tmavé",
    themeParchment: "Pergamen",
    themeCustom: {
        title: "Vlastní pozadí",
        description: "Nahrajte si vlastní obrázek a dejte svému odpočtu jedinečný vzhled. Zde uvidíte, jak bude vypadat na různých zařízeních.",
        previewMobile: "Mobil",
        previewDesktop: "Desktop",
        previewTextMobile: "Toto je náhled, jak váš obrázek a text uvidí uživatelé na telefonu.",
        previewTextDesktop: "Toto je náhled, jak váš obrázek a text uvidí uživatelé na počítači.",
    },

    // countdown board
    newCountdown: "Vytvořit nový odpočet",
    countdownsLocal: "Navštívené odpočty",
    countdownsPublic: "Veřejné odpočty",
    noCountdowns: "Nejsou zde žádné odpočty, je na tobě nějaký vytvořit 😉",
    countdownsError: "Odpočty se bohužel nepodařilo najít 🙁",

    // countdown form
    countdownFormTitle: "Nadpis odpočtu",
    countdownFormWhen: "Kdy se to stane",
    countdownFormMessageAfter: "Text který se zobrazí po vypršení odpočtu",
    countdownFormWhere: "Kde se to stane",
    formDescription: "Popis",
    countdownFormPublic: "Veřejné",
    countdownFormPublicCaption: "Tento odpočet bude možné veřejně vyhledat.",
    countdownFormPrivateCaption: "Tento odpočet uvidí pouze lidé s odkazem.",
    createCountdown: "Vytvořit odpočet",

    formMissingTitle: "Je třeba vyplnit název odpočtu!",
    formMissingCountdown: "Je třeba vyplnit datum a čas odpočtu!",
    fromCountdownError: "Odpočet se bohužel nepodařilo vytvořit 🙁",
    fromCountdownSuccess: "Odpočet byl úspěšně vytvořen 🙂",

    // countdown
    share: "Sdílet",
    countdownError: "Odpočet se bohužel nepodařilo najít 🙁",
    defaultMessageAfter: "Je to tady!",

    // timer
    timersTitle: "Potřebujete pouze nastavit časovač?",
    timerTitle: "Časovač na", // and time
    timerEndMessage: "Čas vypršel 😊",
    timerSettingsTitle: "Vlastní čas odpočtu",
    timerSettingsButton: "Potvrdit",
    timerSettingsOpenButton: "Nastavit",

    day: ["den", "dny", "dny", "dny", "dnů"],
    hour: ["hodina", "hodiny", "hodiny", "hodiny", "hodin"],
    minute: ["minuta", "minuty", "minuty", "minuty", "minut"],
    second: ["vteřina", "vteřiny", "vteřiny", "vteřiny", "vteřin"],

    // poll
    poll: "Hlasování",
    newPoll: "Vytvořit nové hlasování",
    pollsLocal: "Navštívené hlasování",
    pollsPublic: "Veřejné hlasování",
    noPolls: "Žádná hlasování tu zatím nejsou. Je na tobě vytvořit první 😉",
    pollsError: "Omlouváme se, ale hlasování se nepodařilo načíst 🙁",
    pollVote: "Hlasovat",
    pollChangeVote: "Změnit svůj hlas",
    pollNoVote: "Pro tuto možnost zatím nikdo nehlasoval 🤔",
    pollVoteMissing: "Vyber alespoň jednu možnost 🤗",
    pollVoteNotEnough: "Nemáš dostatek hlasů pro výběr další možnosti 🧐",
    pollVoteSuccess: "Váš hlas byl úspěšně zaznamenán!",
    pollVoteError: "Omlouváme se, ale hlasování se nepodařilo odeslat 🙁",
    pollVoteOptions: "Vyber z možností:",
    pollVoteSend: "Odeslat hlas",

    pollVoteCount: "Můžeš použít ještě",
    pollVoteComment: "Přidat komentář",
    vote: ["hlas", "hlasy", "hlasy", "hlasy", "hlasů"],

    // other poll
    pollOther: "Potřebujete jiný typ hlasování?",
    pollWhat: "Univerzální hlasování nebo ankety",
    pollWho: "Hlasování o účasti nebo souhlasu",
    pollWhen: "Hlasování o termínu",

    // poll form
    pollFormTitle: "Název hlasování",
    pollFormDescription: "Popis hlasování",
    pollFormVoteCount: "Kolik možností může hlasující vybrat?",
    pollFormOptions: "Možnosti hlasování",
    pollFormOptionsCaption: "(Každá možnost by měla být unikátní. Jo a na pořadí nezáleží.)",
    pollFormOptionPlaceholder: "Zadej možnost...",
    pollFormPublic: "Veřejné",
    pollFormPublicCaption: "Toto hlasování bude možné veřejně vyhledat.",
    pollFormPrivateCaption: "Toto hlasování uvidí pouze lidé s odkazem.",
    pollCreate: "Vytvořit hlasování",

    pollFormMissingTitle: "Je třeba vyplnit název hlasování!",
    pollFormMissingOptions: "Zadej alespoň dvě možnosti pro hlasování 🧐",
    pollFormError: "Omlouváme se, hlasování se nepodařilo vytvořit 🙁",
    pollFormSuccess: "Hlasování bylo úspěšně vytvořeno 🙂",

    // who
    newWho: "Vytvořit nové hlasování",
    whosLocal: "Navštívené hlasování",
    whosPublic: "Veřejné hlasování",
    noWhos: "Žádná hlasování tu zatím nejsou. Je na tobě vytvořit první 😉",
    whosError: "Omlouváme se, ale hlasování se nepodařilo načíst 🙁",
    whoNoVote: "Pro tuto možnost zatím nikdo nehlasoval 🤔",
    whoVote: "Hlasovat",
    whoChangeVote: "Změnit svůj hlas",
    whoVoteComment: "Přidat komentář (nepovinné)",
    whoVoteMissing: "Vyber jednu z možností 🤗",
    whoVoteError: "Omlouváme se, hlas se nepodařilo odeslat 🙁",
    whoVoteSuccess: "Tvůj hlas byl úspěšně zaznamenán!",
    whoVoteSend: "Odeslat svůj hlas",

    // who form
    whoFormTitle: "Otázka hlasování (název)",
    whoFormDescription: "Popis hlasování",
    whoFormIsMaybeOption: "Povolit možnost \"Možná\"?",
    whoFormWhere: "Kde?",
    whoFormWhen: "Kdy?",
    whoCreate: "Vytvořit hlasování",

    whoFormMissingTitle: "Zadej otázku pro hlasování!",
    whoFormError: "Omlouváme se, ale hlasování se nepodařilo vytvořit 🙁",
    whoFormSuccess: "Hlasování bylo úspěšně vytvořeno 🙂",

    // when
    newWhen: "Vytvořit nové hlasování o termínu",
    whensLocal: "Navštívené hlasování",
    whensPublic: "Veřejné hlasování",
    whensError: "Omlouváme se, nepodařilo se načíst seznam hlasování 🙁 Zkuste to prosím znovu později.",
    noWhens: "Zatím tu nejsou žádná hlasování. Můžete vytvořit první! 😉",

    whenVote: "Hlasovat",
    whenChangeVote: "Změnit svůj hlas",
    whenNoVote: "Pro tuto možnost zatím nikdo nehlasoval 🤔",
    whenVoteMissing: "Všechny moznosti musí být vyplněny!",
    whenVoteSend: "Odeslat svůj hlas",
    whenVoteError: "Omlouváme se, ale hlasování se nepodařilo odeslat 🙁",
    whenVoteSuccess: "Tvůj hlas byl úspěšně zaznamenán!",

    whenLegendYes: "Ano / Můžu / Přijdu",
    whenLegendNo: "Ne / Nemohu / Nepřijdu",
    whenLegendMaybe: "Možná / Podle okolností / Pokud to půjde",
    whenLegendComment: "Přidání komentáře k hlasování",

    // when form
    whenFormTitle: "Název hlasování (např. Název akce)",
    whenFormDescription: "Popis hlasování",
    whenFormIsMaybeOption: "Povolit možnost \"Možná\"?",
    whenFormWhere: "Místo konání",
    whenSelectDates: "Vyberte termíny pro hlasování",
    whenSelectedDates: "Vybrané termíny",
    whenNoSelectedDatesInfo: "Vyberte termín kliknutím do kalendáře.",
    whenCreate: "Vytvořit hlasování",

    whenFormMissingTitle: "Zadej název hlasování!",
    whenFormMissingOptions: "Vyber alespoň dva termíny pro hlasování. 🤗",
    whenFormError: "Omlouváme se, ale hlasování se nepodařilo vytvořit 🙁",
    whenFormSuccess: "Hlasování bylo úspěšně vytvořeno 🙂",

    // team
    teamCreate: "Vytvořit nový tým",
    teamCreateButton: "Vytvořit tým",
    teamEdit: "Upravit tým",
    teamError: "Týmy se bohužel nepodařilo načíst 🙁",
    teamLogIn: ["Abys mohl vytvářet a spravovat týmy, je potřeba ", "přihlásit se"],
    teamEmpty: "Zatím nejsi v žádném týmu. Buď někomu řekni, ať tě pozve, nebo ještě lépe – zkus jeden založit! 😉",
    teamListTitle: "Seznam týmů, ve kterých jsi členem",
    teamName: "Název týmu",
    teamFormDescription: "Popis týmu",
    teamMemberShow: "Zobrazit členy týmu",
    teamMemberEdit: "Spravovat členy týmu",
    teamMemberNotText: "Vypadá to, že nejsi členem tohoto týmu. Chceš se přidat?",
    teamMemberRequired: "Abys mohl hlasovat, musíš být členem týmu.",
    teamMemberAddButton: "Přidat se k týmu",
    teamMemberHeader: "Členové týmu",
    teamMemberRemoveText: "Opravdu chceš vyhodit tohoto člena z týmu?",
    teamMemberLeaveText: "Opravdu chceš opustit tento báječný tým?",
    teamMemberLeaveButton: "Opustit tým",
    getNumberTeamMembers: (count: number) => count === 0
        ? <>V týmu <b>nikdo</b> není.</>
        : count === 1 ? <>V týmu je <b>1</b> člen.</>
            : count <= 4 ? <>V týmu jsou <b>{count}</b> členové.</>
                : <>V týmu je <b>{count}</b> členů.</>,

    teamUserNameEmptyText: "Abys mohl pracovat s týmem, prosím, vyplň své jméno (nebo přezdívku) 🧐",
    teamUserLoginText: ["Pokud bys chtěl spravovat své týmy a hlasování i z jiných zařízení, ", "přihlaš se"],

    teamEventAdd: "Přidat událost",
    teamEventEdit: "Upravit událost",
    teamEventPast: "Proběhlé události",
    teamEventFollowing: "Následující události",
    teamEventEmpty: "Zatím zde nejsou žádné události",
    teamEventDetail: "Detail události",
    teamEventComment: "Komentář k tvé účasti",
    teamEventCommentSave: "Uložit komentář",
    teamEventFormDate: "Datum a čas události",
    teamEventFormDescription: "Popis události",
    teamEventRemoveConfirm: "Opravdu chceš smazat tuto událost?",

    teamAlertPermissionRemove: "Nemáš oprávnění odstraňovat členy z týmu.",
    teamAlertPermissionAdmin: "Nemáš oprávnění měnit správce týmu.",
    teamAlertLastAdmin: "Nemůžeš odebrat posledního správce týmu! Kdo by to pak spravoval? 😉",
    teamErrorMessage: "Něco se pokazilo, zkus to prosím později 🙁",

    // footer
    created: "Vytvořil",
    donateButton: ["☕ Kup Filipovi kafe", "🚀 Podpoř vývoj", "📝 Navrhni vylepšení"],

    // SEO
    seo: {
        index: {
            title: "Titimeme.com | Organizace Času, Událostí a Hlasování",
            description: "Bezplatné online nástroje pro snadnou organizaci času, událostí a hlasování. Vytvářejte odpočty, ankety a plánujte termíny bez registrace a reklam.",
        },
        donate: {
            title: "Kup mi kafe a vylepši titimeme.com | Nástroje zdarma a bez reklam",
            description: "Jsem Filip a stvořil jsem titimeme.com, abych zkrotil chaos v plánování. Podpoř můj projekt jednoho člověka a navrhni vylepšení. Společně to udržíme zdarma!",
        },
        forgottenPassword: {
            title: "Nastavení nového hesla | Titimeme.com",
            description: "Dokončete obnovu hesla. Na této stránce si můžete nastavit nové heslo ke svému účtu na Titimeme.com.",
            text: "Skoro hotovo! Teď už si jen stačí nastavit nové heslo. Zadej ho prosím dvakrát pro kontrolu a budeš mít hotovo. 😉",
        },

        countdown: {
            getTitle: (title?: string) => title ? `${title} | Odpočet` : "Odpočet času online",
            getDescription: (title?: string, description?: string, where?: string) => description
                ? description + " | Odpočet času"
                : `Odpočet k události: ${title}. ${where ? `Na místě: ${where}. ` : ""}Sledujte a sdílejte čas na titimeme.com.`,
        },
        timer: {
            getTitle: (title: string) => `${title} | online`,
            getDescription: (title: string) => `${title} Sledujte a sdílejte čas na titimeme.com.`,
        },
        countdownBoard: {
            title: "Odpočet času online",
            description: "Vytvořte si zdarma stylový odpočet pro vaše akce nebo událostí. Nastavte čas, místo a jednoduše sdílejte s kýmkoli. Funguje perfektně na všech zařízeních!",
            text: <>Těšíte se na důležitou událost? S naším nástrojem můžete vytvořit elegantní <strong>online odpočet</strong> a <strong>sdílet ho s kýmkoli</strong> chcete. Sledujte vteřiny do narozenin, dovolené, startu vašeho projektu nebo jakékoli jiné významné chvíle.</>,
        },
        countdownForm: {
            title: "Vytváření odpočtu času online",
            description: "Vytvořte si zdarma stylový odpočet pro vaše akce nebo událostí. Nastavte čas, místo a jednoduše sdílejte s kýmkoli. Funguje perfektně na všech zařízeních!",
        },

        poll: {
            getTitle: (title?: string) => title ? `${title} | Hlasování online` : "Online hlasování zdarma",
            getDescription: (title?: string, description?: string) => description
                ? description + " | Snadné online hlasování na titimeme.com"
                : `Hlasování: ${title}. Hlasování online a bez reklam titimeme.com.`,
        },
        pollBoard: {
            title: "Hlasování a Ankety online",
            description: "Vytvořte si online hlasování nebo anketu zdarma. Bez registrace, jednoduché nastavení a snadné sdílení. Ideální pro rychlé rozhodování. Funguje na všech zařízeních.",
            text: <>Potřebujete zjistit názor skupiny, ale nechcete se ztrácet v nekonečných zprávách? Tento nástroj vám umožní <strong>vytvořit jednoduchou online anketu nebo hlasování</strong> na jakékoli téma s vlastními možnostmi odpovědí.</>,
        },
        pollForm: {
            title: "Vytvořit hlasování online",
            description: "Vytvořte si vlastní online hlasování zdarma. Bez registrace, jednoduché nastavení, okamžité sdílení. Ideální pro rychlé rozhodování!",
        },

        who: {
            getTitle: (title?: string) => title ? `${title} | Hlasování online` : "Online hlasování zdarma",
            getDescription: (title?: string, description?: string) => description
                ? description + " | Online hlasování na titimeme.com"
                : `Hlasování: ${title}. Zjistěte snadno kdo může nebo souhlasí na titimeme.com.`,
        },
        whoBoard: {
            title: "Hlasování o účasti nebo souhlasu",
            description: "Zjistěte rychle účast nebo získejte souhlas pomocí Ano/Ne/Možná hlasování. Ideální pro organizaci akcí. Bez registrace a zdarma.",
            text: <>Organizujete akci a potřebujete rychle vědět, <strong>kdo dorazí?</strong> Nebo chcete získat jednoduchou zpětnou vazbu formou <strong>Ano / Možná / Ne</strong>? Zjednodušte si zjišťování účasti nebo rychlé rozhodování ve skupině.</>,
        },
        whoForm: {
            title: "Hlasování o účasti nebo souhlasu",
            description: "Zjistěte rychle účast nebo získejte souhlas pomocí Ano/Ne/Možná hlasování. Ideální pro organizaci akcí. Bez registrace a zdarma.",
        },

        when: {
            getTitle: (title?: string) => title ? `${title} | Hlasování o termínu` : "Online hlasování o termínu",
            getDescription: (title?: string, description?: string) => description
                ? `${description} | Hlasujte o nejlepším termínu pro ${title}. Snadné online plánování na titimeme.com.`
                : `Hlasujte o nejlepším termínu pro ${title || 'tuto událost'}. Jednoduché online plánování a koordinace akcí na titimeme.com.`
        },
        whenBoard: {
            title: "Hlasování o termínu",
            description: "Procházejte nebo vytvářejte online hlasování pro nalezení nejlepšího termínu pro vaše akce a setkání. Snadné plánování s přáteli a kolegy.",
            text: <>Skončete s nekonečným dohadováním o tom, <strong>kdy se sejdete!</strong> Nástroj vám umožní navrhnout několik možných dat a časů a nechat ostatní <strong>jednoduše zahlasovat</strong> Ano, Ne nebo Možná. Navíc ke každé volbě mohou <strong>přidat poznámku</strong>. Jednoduchá <strong>alternativa k Doodle</strong>.</>,
        },
        whenForm: {
            title: "Vytvořit nové hlasování o termínu",
            description: "Snadno vytvořte online hlasování pro výběr data vaší příští akce, schůzky nebo setkání. Zjistěte dostupnost účastníků během pár kliknutí.",
        },

        teamBoard: {
            title: "Online organizace týmů a skupin",
            description: "Vytvořte tým pro váš sport, kroužek nebo partu přátel. Snadno plánujte události, sledujte docházku a mějte vše přehledně na jednom místě.",
            text: <>Potřebujete organizovat <strong>tréninky</strong> sportovního týmu, divadelní <strong>zkoušky</strong> nebo jen partu přátel na <strong>pravidelné akce</strong>? Vytvořte si <strong>vlastní tým</strong>, plánujte události a <strong>sledujte účast</strong> všech členů na jednom místě. <strong>Jednoduché, přehledné a efektivní.</strong></>,
        },
        team: {
            getTitle: (title?: string) => title ? `${title} | Týmová organizace` : "Organizace skupin a týmů",
            getDescription: (title?: string, description?: string) => description
                ? `${description} | Tým ${title} na Titimeme.com.`
                : `Přehled týmu ${title}. Plánujte události a sledujte docházku na Titimeme.com.`
        }
    },
};

export const enDict: Dictionary = {
    url: "",
    urlDonate: "buy-coffee",
    urlForgottenPassword: "forgot-password",
    urlCountdown: "countdown",
    urlTimer: "timer",
    urlPoll: "poll",
    urlWho: "who",
    urlWhen: "when",
    urlTeam: "team",
    urlNew: "new",

    wizard: {
        cta: "I want to create... ✨",
        title: "What would you like to create?",
        subTitle: "Choose based on what you are planning today",
        countdownTitle: "Countdown Timer",
        countdownDesc: "I want to count down to an event (birthday, vacation, project launch...)",
        whenTitle: "Group Meeting Date (When?)",
        whenDesc: "I need to find the best date for a meeting or party based on when people are free.",
        whoTitle: "Attendance & Consent (Who?)",
        whoDesc: "I want to see who will attend an event or who agrees with a proposed decision.",
        pollTitle: "Custom Poll / Voting (What?)",
        pollDesc: "I need to let people vote on custom options (choosing lunch, a movie, a gift...).",
        teamTitle: "Team & Attendance (Team!)",
        teamDesc: "I organize regular events (trainings, band rehearsals) and want to track attendance.",
    },

    // general
    localDataInfo: "This list is stored in your browser.",
    save: "Save",
    change: "Change",
    cancel: "Cancel",
    close: "Close",

    yes: "Yes",
    no: "No",
    maybe: "Maybe",

    firstDayOfWeek: 0, // 0 - Sunday, 1 - Monday (standart in USA)
    daysOfWeek: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    daysOfWeekShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    month: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    monthCalendar: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], // English doesn't typically use a different case here

    getNumberVoters: (count: number) => count === 0
        ? <><b>Nobody</b>  has voted yet.</>
        : count === 1 ? <><b>1</b> person has voted.</>
            : <><b>{count}</b> people have voted.</>,

    errorBoundary: {
        title: "Oops, something went wrong here",
        apology: "We apologize for the inconvenience. The application encountered an unexpected problem.",
        action: ["Please try", "refreshing the page", "or go", "back to the homepage", "."],
        info: "If the problem persists, the error has likely been logged automatically, and we're already aware of it. We'll fix it soon!",
    },

    // user
    userNameUnknown: "Unknown User",
    userNameMissing: "Please enter your name (or nickname) 🧐",
    userNamePlaceholder: "Your name (or nickname)...",
    userMonogram: "Initials",
    userEmoji: "Emoji",
    userBackground: "Background Color",
    userMonogramPlaceholder: "Enter your own initials or emoji...",

    userDialogLoggedInHeader: "You are logged in as:",
    userCreateAccount: "Create Account",
    userDialogText: "An account is mainly here so you can use titimeme.com conveniently across multiple devices.",

    userAdmin: "Admin",
    userNotMember: "Not a member",

    mail: "E-mail",
    password: "Password",
    passwordAgain: "Password again",
    userWrongPasswordMatch: "The passwords do not match 🧐",
    userWrongPasswordAgain: "Please enter the password again",
    userWrongPasswordLength: "Password must be at least 6 characters long",
    userWrongMailFormat: "The e-mail format is incorrect 🧐",
    userWrongMailEmpty: "Please enter your e-mail",
    userWrongFill: "Please fill in your login details",
    userErrorLogIn: "Login details are incorrect 🙁",
    userErrorRegister: "Unfortunately, the account could not be created 🙁",
    userWrongSendMail: "Sorry, we don't have this e-mail in our system 🧐",

    userForgottenPassword: "Forgotten Password",
    userForgottenPasswordButton: "I forgot my password 🙁",
    userForgottenPasswordSuccess: "A password reset link has been sent to your e-mail 😉",
    userForgottenPasswordSuccessChange: "Your password has been changed successfully. 😉",
    userForgottenPasswordError: "Unfortunately, something went wrong. Please try again later 🙁",
    userForgottenPasswordErrorChange: "Password change failed. Please try sending the reset link again 🙁",
    userForgottenPasswordErrorSend: "Send password reset link",
    userForgottenPasswordChange: "Change Password",

    allRight: "Everything is alright.",
    logOut: "Log Out",
    logIn: "Log In",
    registerIn: "Start using titimeme.com to the fullest",

    // not found
    notFound: "404 - Page Not Found",
    backToHome: "Back to Homepage",

    // popup
    popup: <><b>That's not all!</b> 😉 Titimeme is a full set of tools for easier planning. Click to explore them, it's all free!</>,

    // messenger redirect
    messengerRedirectInfo: <>To use all features, open <span className="titimeme"><span>ti</span><b>time</b><span>me</span></span> in a standard browser.</>,
    messengerRedirectButton: "Open in Browser",
    messengerRedirectHelp: <>Click on <b>⋯</b> or <b>🧭</b> and select <b>"Open in Browser"</b></>,

    // home screen
    countdown: "When!",
    countdownSubTitle: "Countdown Timer",
    countdownDescription: "Create a stylish countdown for any event and share the excitement with others. Perfect for birthdays, holidays, or project launches.",
    when: "When?",
    whenSubTitle: "Date Finder",
    whenDescription: "Are you planning a meeting or event and can't find a common date? Suggest several options and let others vote Yes, No, or Maybe for each date. A simple alternative to Doodle.",
    who: "Who?",
    whoSubTitle: "Attendance or Agreement",
    whoDescription: "The perfect tool to quickly check attendance or agreement. Who's coming to the party? Who agrees with the proposal? Simply vote Yes, No, or Maybe.",
    where: "Where?",
    whereSubTitle: "Location",
    whereDescription: "Soon you'll be able to easily vote on the best location for your event too.",
    what: "What?",
    whatSubTitle: "Voting and Polls",
    whatDescription: "Need to find out a group's opinion on anything? Create a quick poll with custom answer options. Great for choosing a movie, discussion topic, or meeting place.",
    team: "Team!",
    teamSubTitle: "Group Organization",
    teamDescription: "A tool for effective organization of regular activities such as sports training, theater rehearsals, or other group events.",
    preparing: "Coming soon...",

    home: {
        subTitle: "Easy online planner of time, events, and voting. Free and without registration.",
        text: <>A set of <strong>modern</strong>, <strong>intuitive</strong>, and completely <strong>free</strong> online tools designed to make <strong>planning and sharing with friends, colleagues, or family</strong> as easy as possible.</>,
        words: ["Simplicity", "No registration", "Free", "Ad-free", "Easy sharing"],
        wordsText: [
            "All tools are designed for maximum intuitive operation.", // Corresponds to Simplicity
            "No lengthy sign-ups. Create and share in moments.", // Corresponds to No registration
            "Use all tools and features completely free of charge.", // Corresponds to Free
            "Use all features to the fullest without annoying ads interrupting you.", // Corresponds to Ad-free
            "Every created tool has a unique link for simple sharing.", // Corresponds to Easy sharing
        ],
    },

    // donate page
    donate: "Buy coffee",

    // theme
    theme: "Theme",
    themeSystem: "System Default",
    themeLight: "Light",
    themeDark: "Dark",
    themeParchment: "Parchment",
    themeCustom: {
        title: "Custom Background",
        description: "Upload your own image to give your countdown a unique look. Here you can see how it will appear on different devices.",
        previewMobile: "Mobile",
        previewDesktop: "Desktop",
        previewTextMobile: "This is a preview of how your image and text will look on a mobile phone.",
        previewTextDesktop: "This is a preview of how your image and text will look on a desktop computer.",
    },

    // countdown board
    newCountdown: "Create New Countdown",
    countdownsLocal: "Visited Countdowns",
    countdownsPublic: "Public Countdowns",
    noCountdowns: "There are no countdowns here yet! Feel free to create one 😉",
    countdownsError: "Unfortunately, the countdowns could not be found 🙁",

    // countdown form
    countdownFormTitle: "Countdown Title",
    countdownFormWhen: "When is it happening?",
    countdownFormMessageAfter: "Text to display after the countdown is finished",
    countdownFormWhere: "Where is it happening?",
    formDescription: "Description",
    countdownFormPublic: "Public",
    countdownFormPublicCaption: "This countdown will be publicly searchable.",
    countdownFormPrivateCaption: "Only people with the link will see this countdown.",
    createCountdown: "Create Countdown",

    formMissingTitle: "The title of the countdown is required!",
    formMissingCountdown: "The date and time of the countdown are required!",
    fromCountdownError: "Unfortunately, the countdown could not be created 🙁",
    fromCountdownSuccess: "The countdown has been created successfully!",

    // countdown
    share: "Share",
    countdownError: "Unfortunately, the countdown could not be found 🙁",
    defaultMessageAfter: "It's here!",

    // timer
    timersTitle: "Just need to set a timer?",
    timerTitle: "Timer for", // and time
    timerEndMessage: "Time's up! 😊",
    timerSettingsTitle: "Custom Timer",
    timerSettingsButton: "Confirm",
    timerSettingsOpenButton: "Set Up",

    day: ["day", "days"],
    hour: ["hour", "hours"],
    minute: ["minute", "minutes"],
    second: ["second", "seconds"],

    // poll
    poll: "Poll",
    newPoll: "Create a new poll",
    pollsLocal: "Visited Polls",
    pollsPublic: "Public Polls",
    noPolls: "There are no polls here yet. It's up to you to create the first one 😉",
    pollsError: "Sorry, but the poll could not be loaded 🙁",
    pollVote: "Vote",
    pollChangeVote: "Change your vote",
    pollNoVote: "No one has voted for this option yet 🤔",
    pollVoteMissing: "Please select at least one option 🤗",
    pollVoteNotEnough: "You ran out of votes 🧐",
    pollVoteSuccess: "Your vote has been successfully recorded!",
    pollVoteError: "Sorry, but the vote could not be recorded 🙁",
    pollVoteOptions: "Select from the options:",
    pollVoteSend: "Submit vote",

    pollVoteCount: "You can still use",
    pollVoteComment: "Add comment",
    vote: ["vote", "votes"],

    // other poll
    pollOther: "Need a different type of poll?",
    pollWhat: "Universal voting or polls",
    pollWho: "Voting on attendance or agreement",
    pollWhen: "Voting on a date",

    // poll form
    pollFormTitle: "Poll Title",
    pollFormDescription: "Poll Description",
    pollFormVoteCount: "How many options can voters select?",
    pollFormOptions: "Voting Options",
    pollFormOptionsCaption: "(Each option should be unique.  And the order doesn't matter.)",
    pollFormOptionPlaceholder: "Enter option...",
    pollFormPublic: "Public",
    pollFormPublicCaption: "This poll will be publicly searchable.",
    pollFormPrivateCaption: "Only people with the link will see this poll.",
    pollCreate: "Create Poll",

    pollFormMissingTitle: "Poll title is required!",
    pollFormMissingOptions: "Enter at least two options for the poll 🧐",
    pollFormError: "Sorry, the poll could not be created 🙁",
    pollFormSuccess: "The poll was successfully created 🙂",

    // who
    newWho: "Create New Poll",
    whosLocal: "Visited Polls", // or "My Polls" / "Saved Polls"?
    whosPublic: "Public Polls",
    noWhos: "No polls here yet. Be the first to create one! 😉",
    whosError: "Sorry, we couldn't load the poll. 🙁",
    whoNoVote: "No one has voted for this option yet. 🤔",
    whoVote: "Vote",
    whoChangeVote: "Change Your Vote",
    whoVoteComment: "Add a comment (optional)",
    whoVoteMissing: "Please select an option. 🤗",
    whoVoteError: "Sorry, your vote couldn't be sent. 🙁",
    whoVoteSuccess: "Your vote has been recorded!",
    whoVoteSend: "Submit Your Vote",

    // who form
    whoFormTitle: "Poll Question (Title)",
    whoFormDescription: "Poll Description",
    whoFormIsMaybeOption: "Allow 'Maybe' option?",
    whoFormWhere: "Where?",
    whoFormWhen: "When?",
    whoCreate: "Create Poll",

    whoFormMissingTitle: "Please enter a question for the poll!",
    whoFormError: "Sorry, we couldn't create the poll. 🙁",
    whoFormSuccess: "Poll created successfully! 🙂",

    // when
    newWhen: "Create a new date poll",
    whensLocal: "Visited Polls", // or "My Polls" / "Saved Polls"?
    whensPublic: "Public Polls",
    whensError: "Sorry, we couldn't load the poll list 🙁 Please try again later.",
    noWhens: "There are no polls here yet. You can create the first one! 😉",

    whenVote: "Vote",
    whenChangeVote: "Change my vote",
    whenNoVote: "No one has voted for this option yet 🤔",
    whenVoteMissing: "All options must be filled!", // Literal translation, consider context - maybe "Please make a selection!"
    whenVoteSend: "Submit my vote",
    whenVoteError: "Sorry, your vote could not be submitted 🙁",
    whenVoteSuccess: "Your vote has been successfully recorded!",

    whenLegendYes: "Yes / I can / I'll attend",
    whenLegendNo: "No / I can't / I won't attend",
    whenLegendMaybe: "Maybe / Depends / If possible",
    whenLegendComment: "Add a comment",

    // when form
    whenFormTitle: "Poll Title (e.g., Event Name)",
    whenFormDescription: "Poll Description",
    whenFormIsMaybeOption: "Allow \"Maybe\" option?",
    whenFormWhere: "Location",
    whenSelectDates: "Select dates for voting",
    whenSelectedDates: "Selected dates",
    whenNoSelectedDatesInfo: "Select a date by clicking on the calendar.",
    whenCreate: "Create Poll",

    whenFormMissingTitle: "Enter the poll title!",
    whenFormMissingOptions: "Select at least two dates for voting. 🤗",
    whenFormError: "Sorry, the poll could not be created 🙁",
    whenFormSuccess: "Poll created successfully 🙂",

    // team
    teamCreate: "Create New Team",
    teamCreateButton: "Create Team",
    teamEdit: "Edit Team",
    teamError: "Unfortunately, the teams could not be loaded 🙁",
    teamLogIn: ["To create and manage teams, you need to ", "log in"],
    teamEmpty: "You're not in any team yet. Either ask someone to invite you, or even better — try creating one! 😉",
    teamListTitle: "List of teams you are a member of",
    teamName: "Team Name",
    teamFormDescription: "Team Description",
    teamMemberShow: "Show team members",
    teamMemberEdit: "Manage team members",
    teamMemberNotText: "It looks like you're not a member of this team. Want to join?",
    teamMemberRequired: "To vote, you must be a member of the team.",
    teamMemberAddButton: "Join Team",
    teamMemberHeader: "Team Members",
    teamMemberRemoveText: "Are you sure you want to remove this member from the team?",
    teamMemberLeaveText: "Are you sure you want to leave this amazing team?",
    teamMemberLeaveButton: "Leave Team",
    getNumberTeamMembers: (count: number) => count === 0
        ? <>There is <b>nobody</b> in the team.</>
        : count === 1 ? <>There is <b>1</b> member in the team.</>
            : <>There are <b>{count}</b> members in the team.</>,

    teamUserNameEmptyText: "To work with the team, please fill in your name (or nickname) 🧐",
    teamUserLoginText: ["If you'd like to manage your teams and votes from other devices, ", "log in"],

    teamEventAdd: "Add Event",
    teamEventEdit: "Edit Event",
    teamEventPast: "Past Events",
    teamEventFollowing: "Upcoming Events",
    teamEventEmpty: "There are no events here yet",
    teamEventDetail: "Event Details",
    teamEventComment: "Comment on your attendance",
    teamEventCommentSave: "Save Comment",
    teamEventFormDate: "Event Date and Time",
    teamEventFormDescription: "Event Description",
    teamEventRemoveConfirm: "Do you really want to delete this event?",

    teamAlertPermissionRemove: "You don't have permission to remove members from the team.",
    teamAlertPermissionAdmin: "You don't have permission to change team admins.",
    teamAlertLastAdmin: "You can't remove the last admin from the team! Who would manage it then? 😉",
    teamErrorMessage: "Something went wrong, please try again later 🙁",

    // footer
    created: "Created by",
    donateButton: ["☕ Buy Filip a coffee", "🚀 Support development", "📝 Suggest an improvement"],


    // SEO
    seo: {
        index: {
            title: "Titimeme.com | Time, Event, and Voting Organization",
            description: "Titimeme.com offers free online tools for easy organization of time, events, and voting. Create countdowns, polls, and schedule dates without registration and ads.",
        },
        donate: {
            title: "Buy me coffee and improve titimeme.com | Free tools without ads",
            description: "I'm Filip and I created titimeme.com to tame the chaos in planning. Support my one-person project and suggest improvements. Together we'll keep it free!",
        },
        forgottenPassword: {
            title: "Set New Password | Titimeme.com",
            description: "Complete your password recovery. On this page, you can set a new password for your Titimeme.com account.",
            text: "Almost done! Now just set your new password. Please enter it twice to confirm, and you'll be all set. 😉",
        },

        countdown: {
            getTitle: (title?: string) => title ? `${title} | Countdown` : "Online Countdown Timer",
            getDescription: (title?: string, description?: string, where?: string) => description
                ? description + " | Countdown Timer"
                : `Countdown to event: ${title}. ${where ? `Location: ${where}. ` : ""}Track and share time on titimeme.com.`,
        },
        timer: {
            getTitle: (title: string) => `${title} | online`,
            getDescription: (title: string) => `${title} Track and share time on titimeme.com.`,
        },
        countdownBoard: {
            title: "Online Countdown Timer",
            description: "Create a free, stylish countdown for your events. Set the time, place, and easily share with anyone. Works perfectly on all devices!",
            text: <>Looking forward to an important event? With our tool, you can create an elegant <strong>online countdown</strong> and <strong>share it with anyone</strong> you want. Track the seconds until birthdays, holidays, project launches, or any other significant moment.</>,
        },
        countdownForm: {
            title: "Create Online Countdown Timer",
            description: "Create a free, stylish countdown for your events. Set the time, place, and easily share with anyone. Works perfectly on all devices!",
        },

        poll: {
            getTitle: (title?: string) => title ? `${title} | Online Poll` : "Free Online Poll",
            getDescription: (title?: string, description?: string) => description
                ? description + " | Easy online voting on titimeme.com"
                : `Poll: ${title}. Online and ad-free polling on titimeme.com.`,
        },
        pollBoard: {
            title: "Online Voting and Polls",
            description: "Create your online poll or survey for free. No registration, simple setup, and easy sharing. Ideal for quick decisions. Works on all devices.",
            text: <>Need to find out a group's opinion but don't want to get lost in endless messages? This tool allows you to <strong>create a simple online poll or survey</strong> on any topic with custom answer options.</>,
        },
        pollForm: {
            title: "Create Online Poll",
            description: "Create your custom online poll for free. No registration, simple setup, instant sharing. Ideal for quick decisions!",
        },

        who: {
            getTitle: (title?: string) => title ? `${title} | Online Poll` : "Free Online Poll",
            getDescription: (title?: string, description?: string) => description
                ? description + " | Online polling on titimeme.com"
                : `Poll: ${title}. Easily find out who can attend or agrees on titimeme.com.`,
        },
        whoBoard: {
            title: "Attendance or Agreement Poll",
            description: "Quickly check attendance or get agreement using a Yes/No/Maybe poll. Ideal for event organization. No registration and free.",
            text: <>Organizing an event and need to know quickly <strong>who's coming?</strong> Or want simple feedback in the form of <strong>Yes / Maybe / No</strong>? Simplify checking attendance or quick group decision-making.</>,
        },
        whoForm: {
            title: "Attendance or Agreement Poll",
            description: "Quickly check attendance or get agreement using a Yes/No/Maybe poll. Ideal for event organization. No registration and free.",
        },

        when: {
            getTitle: (title?: string) => title ? `${title} | Date Poll` : "Online Date Poll",
            getDescription: (title?: string, description?: string) => description
                ? `${description} | Vote for the best date for ${title}. Easy online planning on titimeme.com.`
                : `Vote for the best date for ${title || 'this event'}. Simple online planning and event coordination on titimeme.com.`
        },
        whenBoard: {
            title: "Date Poll",
            description: "Browse or create online polls to find the best date for your events and meetings. Easy planning with friends and colleagues.",
            text: <>Stop the endless back-and-forth about <strong>when to meet!</strong> This tool lets you propose several possible dates and times and let others <strong>simply vote</strong> Yes, No, or Maybe. Plus, they can <strong>add a note</strong> to each choice. A simple <strong>alternative to Doodle</strong>.</>,
        },
        whenForm: {
            title: "Create New Date Poll",
            description: "Easily create an online poll to choose the date for your next event, meeting, or gathering. Find out participant availability in just a few clicks.",
        },

        teamBoard: {
            title: "Online Team and Group Organization",
            description: "Create a team for your sport, club, or group of friends. Easily schedule events, track attendance, and keep everything organized in one place.",
            text: <>Need to organize sports team <strong>practices</strong>, theater <strong>rehearsals</strong>, or just <strong>regular events</strong> for a group of friends? Create your <strong>own team</strong>, schedule events, and <strong>track attendance</strong> all in one place. <strong>Simple, clear, and effective.</strong></>,
        },
        team: {
            getTitle: (title?: string) => title ? `${title} | Team Organization` : "Team and Group Organization",
            getDescription: (title?: string, description?: string) => description
                ? `${description} | Team ${title} on Titimeme.com.`
                : `Team overview for ${title}. Schedule events and track attendance on Titimeme.com.`
        },
    },
};

export const esDict: Dictionary = {
    url: "es",
    urlDonate: "invitame-un-cafe",
    urlForgottenPassword: "contrasena-olvidada",
    urlCountdown: "cuenta-regresiva",
    urlTimer: "temporizador",
    urlPoll: "encuesta",
    urlWho: "quien",
    urlWhen: "cuando",
    urlTeam: "equipo",
    urlNew: "nuevo",

    wizard: {
        cta: "Quiero crear algo... ✨",
        title: "¿Qué te gustaría crear?",
        subTitle: "Elige según lo que estés planeando hoy",
        countdownTitle: "Cuenta Regresiva",
        countdownDesc: "Quiero contar el tiempo para un evento (cumpleaños, vacaciones, lanzamiento...)",
        whenTitle: "Fecha de Reunión (¿Cuándo?)",
        whenDesc: "Necesito encontrar la mejor fecha para una reunión o fiesta según la disponibilidad.",
        whoTitle: "Asistencia y Consentimiento (¿Quién?)",
        whoDesc: "Quiero saber quién asistirá a un evento o quién está de acuerdo con una propuesta.",
        pollTitle: "Encuesta / Votación (¿Qué?)",
        pollDesc: "Necesito que la gente vote sobre opciones personalizadas (elegir comida, película...).",
        teamTitle: "Equipo y Asistencia (¡Equipo!)",
        teamDesc: "Organizo eventos regulares (entrenamientos, ensayos) y quiero rastrear la asistencia.",
    },

    // general
    localDataInfo: "Esta lista se guarda en tu navegador.",
    save: "Guardar",
    change: "Cambiar",
    cancel: "Cancelar",
    close: "Cerrar",

    yes: "Sí",
    no: "No",
    maybe: "Tal vez",

    firstDayOfWeek: 1, // 0 - domingo, 1 - lunes (standard in Spain/Latin America)
    daysOfWeek: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
    daysOfWeekShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"], // Note accents
    month: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"],
    monthCalendar: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"], // Spanish uses "de" + month name, base name is sufficient

    getNumberVoters: (count: number) => count === 0
        ? <><b>Nadie</b> ha votado todavía.</>
        : count === 1 ? <>Ha votado <b>1</b> persona.</>
            : <>Han votado <b>{count}</b> personas.</>,

    errorBoundary: {
        title: "Vaya, algo salió mal aquí",
        apology: "Lamentamos las molestias. La aplicación ha encontrado un problema inesperado.",
        action: ["Por favor, intenta", "refrescar la página", "o vuelve", "a la página de inicio", "."],
        info: "Si el problema persiste, es probable que el error se haya registrado automáticamente y ya lo sabemos. ¡Lo arreglaremos pronto!",
    },

    // user
    userNameUnknown: "Anónimo",
    userNameMissing: "Por favor, introduce tu nombre (o apodo) 🧐",
    userNamePlaceholder: "Tu nombre (o apodo)...",
    userMonogram: "Iniciales",
    userEmoji: "Emoji",
    userBackground: "Color de fondo",
    userMonogramPlaceholder: "Introduce iniciales o emoji...",

    userDialogLoggedInHeader: "Has iniciado sesión como:",
    userCreateAccount: "Crear cuenta",
    userDialogText: "La cuenta sirve principalmente para que puedas usar titimeme.com cómodamente en múltiples dispositivos.",

    userAdmin: "Admin",
    userNotMember: "No es miembro",

    mail: "E-mail",
    password: "Contraseña",
    passwordAgain: "Contraseña de nuevo",
    userWrongPasswordMatch: "Las contraseñas no coinciden 🧐",
    userWrongPasswordAgain: "Por favor, introduce la contraseña de nuevo",
    userWrongPasswordLength: "La contraseña debe tener al menos 6 caracteres",
    userWrongMailFormat: "El formato del e-mail es incorrecto 🧐",
    userWrongMailEmpty: "Por favor, introduce tu e-mail",
    userWrongFill: "Por favor, rellena tus datos de acceso",
    userErrorLogIn: "Los datos de acceso son incorrectos 🙁",
    userErrorRegister: "Lamentablemente, no se pudo crear la cuenta 🙁",
    userWrongSendMail: "Lo sentimos, no tenemos este e-mail en nuestro sistema 🧐",

    userForgottenPassword: "Contraseña Olvidada",
    userForgottenPasswordButton: "Olvidé mi contraseña 🙁",
    userForgottenPasswordSuccess: "Se ha enviado un enlace para cambiar la contraseña a tu e-mail 😉",
    userForgottenPasswordSuccessChange: "¡Tu contraseña se ha cambiado con éxito! 😉",
    userForgottenPasswordError: "Lamentablemente, algo salió mal. Por favor, inténtalo más tarde 🙁",
    userForgottenPasswordErrorChange: "El cambio de contraseña falló. Por favor, intenta enviar el enlace de nuevo 🙁",
    userForgottenPasswordErrorSend: "Enviar enlace de cambio de contraseña",
    userForgottenPasswordChange: "Cambiar Contraseña",

    allRight: "Todo está en orden.",
    logOut: "Cerrar sesión",
    logIn: "Iniciar sesión",
    registerIn: "Empieza a usar titimeme.com al máximo",

    // not found
    notFound: "404 - Página no encontrada",
    backToHome: "Volver a la página principal",

    // popup
    popup: <><b>¡Esto no es todo!</b> 😉 Titimeme es un conjunto completo de herramientas para una planificación más sencilla. ¡Haz clic y explóralas, todo es gratis!</>,

    // messenger redirect
    messengerRedirectInfo: <>Para usar todas las funciones, abre <span className="titimeme"><span>ti</span><b>time</b><span>me</span></span> en un navegador estándar.</>,
    messengerRedirectButton: "Abrir en el navegador",
    messengerRedirectHelp: <>Haz clic en <b>⋯</b> o <b>🧭</b> y selecciona <b>"Abrir en el navegador"</b></>,

    // home screen
    countdown: "¡Cuándo!",
    countdownSubTitle: "Cuenta atrás",
    countdownDescription: "Crea una elegante cuenta atrás para cualquier evento y comparte la emoción con otros. Ideal para cumpleaños, vacaciones o lanzamientos de proyectos.",
    when: "¿Cuándo?",
    whenSubTitle: "Buscar Fecha",
    whenDescription: "¿Planeando una reunión o evento y no encuentras una fecha común? Sugiere varias opciones y deja que otros voten Sí, No o Quizás para cada fecha. Una alternativa simple a Doodle.",
    who: "¿Quién?",
    whoSubTitle: "Asistencia o Acuerdo",
    whoDescription: "La herramienta perfecta para verificar rápidamente la asistencia o el acuerdo. ¿Quién viene a la fiesta? ¿Quién está de acuerdo con la propuesta? Simplemente vota Sí, No o Quizás.",
    where: "¿Dónde?",
    whereSubTitle: "Ubicación",
    whereDescription: "Pronto también podrás votar fácilmente por la mejor ubicación para tu evento.",
    what: "¿Qué?",
    whatSubTitle: "Votaciones y Encuestas",
    whatDescription: "¿Necesitas conocer la opinión de un grupo sobre algo? Crea una encuesta rápida con opciones de respuesta personalizadas. Genial para elegir una película, tema de discusión o lugar de reunión.",
    team: "¡Equipo!",
    teamSubTitle: "Organización de grupos",
    teamDescription: "Una herramienta para la organización eficaz de actividades regulares como entrenamientos deportivos, ensayos de teatro u otros eventos grupales.",
    preparing: "Próximamente...",

    home: {
        subTitle: "Organización fácil de tiempo, eventos y votaciones online. Gratis y sin registro.",
        text: <>Un conjunto de herramientas online <strong>modernas</strong>, <strong>intuitivas</strong> y completamente <strong>gratuitas</strong> diseñadas para facilitar al máximo la <strong>planificación y el compartir con amigos, colegas o familiares.</strong></>,
        words: ["Sencillez", "Sin registro", "Gratis", "Sin anuncios", "Fácil de compartir"],
        wordsText: [
            "Todas las herramientas están diseñadas para un manejo lo más intuitivo posible.", // Corresponds to Sencillez
            "Sin largos procesos de inscripción. Crea y comparte en instantes.", // Corresponds to Sin registro
            "Usa todas las herramientas y funciones de forma totalmente gratuita.", // Corresponds to Gratis
            "Usa todas las funciones al máximo sin que te interrumpan molestos anuncios.", // Corresponds to Sin anuncios
            "Cada herramienta creada tiene un enlace único para compartirla fácilmente.", // Corresponds to Fácil de compartir
        ],
    },

    // donate page
    donate: "Cafecito",

    // theme
    theme: "Tema",
    themeSystem: "Predet. del sistema",
    themeLight: "Claro",
    themeDark: "Oscuro",
    themeParchment: "Pergamino",
    themeCustom: {
        title: "Fondo personalizado",
        description: "Sube tu propia imagen para darle a tu cuenta atrás un aspecto único. Aquí puedes ver cómo se mostrará en diferentes dispositivos.",
        previewMobile: "Móvil",
        previewDesktop: "Escritorio",
        previewTextMobile: "Esta es una vista previa de cómo se verán tu imagen y el texto en un teléfono móvil.",
        previewTextDesktop: "Esta es una vista previa de cómo se verán tu imagen y el texto en un ordenador.",
    },

    // countdown board
    newCountdown: "Crear nueva cuenta regresiva",
    countdownsLocal: "Cuentas atrás visitadas",
    countdownsPublic: "Cuentas atrás públicas",
    noCountdowns: "¡No hay cuentas regresivas aquí! Anímate a crear una 😉",
    countdownsError: "Desafortunadamente, no se pudieron encontrar las cuentas regresivas 🙁",

    // countdown form
    countdownFormTitle: "Título de la cuenta regresiva",
    countdownFormWhen: "¿Cuándo sucederá?",
    countdownFormMessageAfter: "Texto para mostrar después de la cuenta regresiva",
    countdownFormWhere: "¿Dondé va a pasar?",
    formDescription: "Descripción",
    countdownFormPublic: "Público",
    countdownFormPublicCaption: "Esta cuenta atrás se podrá buscar públicamente.",
    countdownFormPrivateCaption: "Solo las personas con el enlace verán esta cuenta atrás.",
    createCountdown: "Crear cuenta regresiva",

    formMissingTitle: "¡Se requiere el título de la cuenta regresiva!",
    formMissingCountdown: "¡Se requiere la fecha y hora de la cuenta regresiva!",
    fromCountdownError: "Desafortunadamente, la cuenta regresiva no se pudo crear 🙁",
    fromCountdownSuccess: "¡La cuenta regresiva se creó con éxito!",

    // countdown
    share: "Compartir",
    countdownError: "Desafortunadamente, no se pudo encontrar la cuenta regresiva 🙁",
    defaultMessageAfter: "¡Ya está aquí!",

    // timer
    timersTitle: "¿Solo necesitas configurar un temporizador?",
    timerTitle: "Temporizador para", // and time
    timerEndMessage: "¡Se acabó el tiempo! 😊",
    timerSettingsTitle: "Temporizador Personalizado",
    timerSettingsButton: "Confirmar",
    timerSettingsOpenButton: "Configurar", // Alternativně "Ajustar"

    day: ["día", "días"],
    hour: ["hora", "horas"],
    minute: ["minuto", "minutos"],
    second: ["segundo", "segundos"],

    // poll
    poll: "Encuesta",
    newPoll: "Crear nueva encuesta",
    pollsLocal: "Encuestas visitadas",
    pollsPublic: "Encuestas públicas",
    noPolls: "Aún no hay encuestas aquí. ¡Te toca crear la primera 😉",
    pollsError: "Lo sentimos, pero la encuesta no se pudo cargar 🙁",
    pollVote: "Votar",
    pollChangeVote: "Cambiar tu voto",
    pollNoVote: "Nadie ha votado por esta opción todavía 🤔",
    pollVoteMissing: "Por favor, selecciona al menos una opción 🤗",
    pollVoteNotEnough: "No tienes suficientes votos para seleccionar otra opción 🧐",
    pollVoteSuccess: "¡Tu voto se ha registrado correctamente!",
    pollVoteError: "Lo sentimos, pero el voto no se pudo enviar 🙁",
    pollVoteOptions: "Selecciona de las opciones:",
    pollVoteSend: "Enviar voto",

    pollVoteCount: "Todavía puedes usar",
    pollVoteComment: "Añadir comentario",
    vote: ["voto", "votos", "votos", "votos", "votos"],

    // other poll
    pollOther: "¿Necesitas otro tipo de votación?",
    pollWhat: "Votación universal o encuestas",
    pollWho: "Votación sobre asistencia o acuerdo",
    pollWhen: "Votación sobre fecha",

    // poll form
    pollFormTitle: "Título de la encuesta",
    pollFormDescription: "Descripción de la encuesta",
    pollFormVoteCount: "¿Cuántas opciones pueden seleccionar los votantes?",
    pollFormOptions: "Opciones de votación",
    pollFormOptionsCaption: "(Cada opción debe ser única. Y el orden no importa.)",
    pollFormOptionPlaceholder: "Introduce opción...",
    pollFormPublic: "Pública",
    pollFormPublicCaption: "Esta encuesta se podrá buscar públicamente.",
    pollFormPrivateCaption: "Solo las personas con el enlace verán esta encuesta.",
    pollCreate: "Crear Encuesta",

    pollFormMissingTitle: "¡Se requiere el título de la encuesta!",
    pollFormMissingOptions: "Introduce al menos dos opciones para la encuesta 🧐",
    pollFormError: "Lo sentimos, la encuesta no se pudo crear 🙁",
    pollFormSuccess: "La encuesta se creó correctamente 🙂",

    // who
    newWho: "Crear Nueva Encuesta",
    whosLocal: "Encuestas visitadas",
    whosPublic: "Encuestas públicas",
    noWhos: "Aún no hay encuestas aquí. ¡Sé el primero en crear una! 😉",
    whosError: "Lo sentimos, no pudimos cargar la encuesta. 🙁",
    whoNoVote: "Nadie ha votado por esta opción todavía. 🤔",
    whoVote: "Votar",
    whoChangeVote: "Cambiar Tu Voto",
    whoVoteComment: "Añadir un comentario (opcional)",
    whoVoteMissing: "Por favor, selecciona una opción. 🤗",
    whoVoteError: "Lo sentimos, tu voto no pudo ser enviado. 🙁",
    whoVoteSuccess: "¡Tu voto ha sido registrado!",
    whoVoteSend: "Enviar Tu Voto",

    // who form
    whoFormTitle: "Pregunta de la Encuesta (Título)",
    whoFormDescription: "Descripción de la Encuesta",
    whoFormIsMaybeOption: "¿Permitir la opción 'Tal vez'?",
    whoFormWhere: "¿Dónde?",
    whoFormWhen: "¿Cuándo?",
    whoCreate: "Crear Encuesta",

    whoFormMissingTitle: "¡Por favor, introduce una pregunta para la encuesta!",
    whoFormError: "Lo sentimos, no pudimos crear la encuesta. 🙁",
    whoFormSuccess: "¡Encuesta creada con éxito! 🙂",

    // when
    newWhen: "Crear nueva encuesta de fecha",
    whensLocal: "Encuestas visitadas",
    whensPublic: "Encuestas públicas",
    whensError: "Lo sentimos, no se pudo cargar la lista de encuestas 🙁 Por favor, inténtalo de nuevo más tarde.",
    noWhens: "Aún no hay encuestas aquí. ¡Puedes crear la primera! 😉",

    whenVote: "Votar",
    whenChangeVote: "Cambiar mi voto",
    whenNoVote: "Nadie ha votado por esta opción todavía 🤔",
    whenVoteMissing: "¡Todas las opciones deben ser completadas!", // Literal translation, consider context - maybe "¡Por favor, haz una selección!"
    whenVoteSend: "Enviar mi voto",
    whenVoteError: "Lo sentimos, tu voto no pudo ser enviado 🙁",
    whenVoteSuccess: "¡Tu voto ha sido registrado con éxito!",

    whenLegendYes: "Sí / Puedo / Asistiré",
    whenLegendNo: "No / No puedo / No asistiré",
    whenLegendMaybe: "Quizás / Depende / Si es posible",
    whenLegendComment: "Añadir comentario",

    // when form
    whenFormTitle: "Título de la encuesta (ej. Nombre del evento)",
    whenFormDescription: "Descripción de la encuesta",
    whenFormIsMaybeOption: "¿Permitir la opción \"Tal vez\"?", // Or "Quizás"
    whenFormWhere: "Lugar", // Or "Ubicación"
    whenSelectDates: "Selecciona las fechas para la votación",
    whenSelectedDates: "Fechas seleccionadas",
    whenNoSelectedDatesInfo: "Selecciona una fecha haciendo clic en el calendario.",
    whenCreate: "Crear encuesta",

    whenFormMissingTitle: "¡Introduce el título de la encuesta!",
    whenFormMissingOptions: "Selecciona al menos dos fechas para la votación. 🤗",
    whenFormError: "Lo sentimos, la encuesta no pudo ser creada 🙁",
    whenFormSuccess: "Encuesta creada con éxito 🙂",

    // team
    teamCreate: "Crear Nuevo Equipo",
    teamCreateButton: "Crear Equipo",
    teamEdit: "Editar Equipo",
    teamError: "Lamentablemente, no se pudieron cargar los equipos 🙁",
    teamLogIn: ["Para crear y gestionar equipos, necesitas ", "iniciar sesión"],
    teamEmpty: "Todavía no estás en ningún equipo. ¡Pide a alguien que te invite, o mejor aún, intenta crear uno! 😉",
    teamListTitle: "Lista de equipos de los que eres miembro",
    teamName: "Nombre del Equipo",
    teamFormDescription: "Descripción del Equipo",
    teamMemberShow: "Mostrar miembros del equipo",
    teamMemberEdit: "Gestionar miembros del equipo",
    teamMemberNotText: "Parece que no eres miembro de este equipo. ¿Quieres unirte?",
    teamMemberRequired: "Para votar, debes ser miembro del equipo.",
    teamMemberAddButton: "Unirse al Equipo",
    teamMemberHeader: "Miembros del Equipo",
    teamMemberRemoveText: "¿Estás seguro de que quieres eliminar a este miembro del equipo?",
    teamMemberLeaveText: "¿Estás seguro de que quieres abandonar este magnífico equipo?",
    teamMemberLeaveButton: "Abandonar Equipo",
    getNumberTeamMembers: (count: number) => count === 0
        ? <>No hay <b>nadie</b> en el equipo.</>
        : count === 1 ? <>Hay <b>1</b> miembro en el equipo.</>
            : <>Hay <b>{count}</b> miembros en el equipo.</>,

    teamUserNameEmptyText: "Para interactuar con el equipo, por favor, rellena tu nombre (o apodo) 🧐",
    teamUserLoginText: ["Si quieres gestionar tus equipos y votaciones desde otros dispositivos, ", "inicia sesión"],

    teamEventAdd: "Añadir Evento",
    teamEventEdit: "Editar Evento",
    teamEventPast: "Eventos Pasados",
    teamEventFollowing: "Próximos Eventos",
    teamEventEmpty: "Todavía no hay eventos aquí",
    teamEventDetail: "Detalles del Evento",
    teamEventComment: "Comentario sobre tu asistencia",
    teamEventCommentSave: "Guardar Comentario",
    teamEventFormDate: "Fecha y Hora del Evento",
    teamEventFormDescription: "Descripción del Evento",
    teamEventRemoveConfirm: "¿Realmente quieres eliminar este evento?",

    teamAlertPermissionRemove: "No tienes permiso para eliminar miembros del equipo.",
    teamAlertPermissionAdmin: "No tienes permiso para cambiar los administradores del equipo.",
    teamAlertLastAdmin: "¡No puedes eliminar al último administrador del equipo! ¿Quién lo gestionaría entonces? 😉",
    teamErrorMessage: "Algo salió mal, por favor inténtalo más tarde 🙁",

    // footer
    created: "Creado por",
    donateButton: ["☕ Invita a Filip a un café", "🚀 Apoya el desarrollo", "📝 Sugiere una mejora"],

    // SEO
    seo: {
        index: {
            title: "Titimeme.com | Organización de Tiempo, Eventos y Votaciones",
            description: "Titimeme.com ofrece herramientas online gratuitas para organizar fácilmente tiempo, eventos y votaciones. Crea cuentas atrás, encuestas y planifica fechas sin registro ni anuncios.",
        },
        donate: {
            title: "Invítame un café y mejora titimeme.com | Herramientas gratis y sin anuncios",
            description: "Soy Filip y creé titimeme.com para domar el caos de la planificación. Apoya mi proyecto de una sola persona y sugiere mejoras. ¡Juntos lo mantendremos gratis!",
        },
        forgottenPassword: {
            title: "Establecer Nueva Contraseña | Titimeme.com",
            description: "Completa la recuperación de tu contraseña. En esta página puedes establecer una nueva contraseña para tu cuenta de Titimeme.com.",
            text: "¡Casi listo! Ahora solo tienes que establecer tu nueva contraseña. Introdúcela dos veces para confirmar y ya está. 😉",
        },

        countdown: {
            getTitle: (title?: string) => title ? `${title} | Cuenta atrás` : "Temporizador de cuenta atrás online",
            getDescription: (title?: string, description?: string, where?: string) => description
                ? description + " | Temporizador de cuenta atrás"
                : `Cuenta atrás para el evento: ${title}. ${where ? `Lugar: ${where}. ` : ""}Sigue y comparte el tiempo en titimeme.com.`,
        },
        timer: {
            getTitle: (title: string) => `${title} | online`,
            getDescription: (title: string) => `${title} Controla y comparte el tiempo en titimeme.com.`,
        },
        countdownBoard: {
            title: "Temporizador de cuenta atrás online",
            description: "Crea gratis una elegante cuenta atrás para tus eventos. Configura la hora, el lugar y compártela fácilmente con quien quieras. ¡Funciona perfectamente en todos los dispositivos!",
            text: <>¿Esperando un evento importante? Con nuestra herramienta, puedes crear una elegante <strong>cuenta atrás online</strong> y <strong>compartirla con quien quieras</strong>. Sigue los segundos hasta cumpleaños, vacaciones, lanzamientos de proyectos o cualquier otro momento significativo.</>,
        },
        countdownForm: {
            title: "Crear Temporizador de cuenta atrás online",
            description: "Crea gratis una elegante cuenta atrás para tus eventos. Configura la hora, el lugar y compártela fácilmente con quien quieras. ¡Funciona perfectamente en todos los dispositivos!",
        },

        poll: {
            getTitle: (title?: string) => title ? `${title} | Votación Online` : "Votación Online Gratis",
            getDescription: (title?: string, description?: string) => description
                ? description + " | Votación online fácil en titimeme.com"
                : `Votación: ${title}. Votaciones online y sin anuncios en titimeme.com.`,
        },
        pollBoard: {
            title: "Votaciones y Encuestas Online",
            description: "Crea tu votación o encuesta online gratis. Sin registro, configuración sencilla y fácil de compartir. Ideal para decisiones rápidas. Funciona en todos los dispositivos.",
            text: <>¿Necesitas conocer la opinión de un grupo pero no quieres perderte en mensajes interminables? Esta herramienta te permite <strong>crear una sencilla encuesta o votación online</strong> sobre cualquier tema con opciones de respuesta personalizadas.</>,
        },
        pollForm: {
            title: "Crear Votación Online",
            description: "Crea tu propia votación online gratis. ¡Sin registro, configuración sencilla, compartición instantánea. Ideal para decisiones rápidas!",
        },

        who: {
            getTitle: (title?: string) => title ? `${title} | Votación Online` : "Votación Online Gratis",
            getDescription: (title?: string, description?: string) => description
                ? description + " | Votación online en titimeme.com"
                : `Votación: ${title}. Averigua fácilmente quién puede asistir o está de acuerdo en titimeme.com.`,
        },
        whoBoard: {
            title: "Votación de Asistencia o Acuerdo",
            description: "Verifica rápidamente la asistencia u obtén acuerdo usando una votación Sí/No/Quizás. Ideal para la organización de eventos. Sin registro y gratis.",
            text: <>¿Organizando un evento y necesitas saber rápidamente <strong>quién asistirá?</strong> ¿O quieres una respuesta simple en forma de <strong>Sí / Quizás / No</strong>? Simplifica la verificación de asistencia o la toma rápida de decisiones en grupo.</>,
        },
        whoForm: {
            title: "Votación de Asistencia o Acuerdo",
            description: "Verifica rápidamente la asistencia u obtén acuerdo usando una votación Sí/No/Quizás. Ideal para la organización de eventos. Sin registro y gratis.",
        },

        when: {
            getTitle: (title?: string) => title ? `${title} | Encuesta de Fecha` : "Encuesta de Fecha Online",
            getDescription: (title?: string, description?: string) => description
                ? `${description} | Vota por la mejor fecha para ${title}. Planificación online fácil en titimeme.com.`
                : `Vota por la mejor fecha para ${title || 'este evento'}. Planificación online sencilla y coordinación de eventos en titimeme.com.`
        },
        whenBoard: {
            title: "Encuesta de Fecha",
            description: "Explora o crea encuestas online para encontrar la mejor fecha para tus eventos y reuniones. Planificación fácil con amigos y colegas.",
            text: <>¡Acaba con las interminables discusiones sobre <strong>cuándo reuniros!</strong> Esta herramienta te permite proponer varias fechas y horas posibles y dejar que otros <strong>voten simplemente</strong> Sí, No o Quizás. Además, pueden <strong>añadir una nota</strong> a cada opción. Una <strong>alternativa simple a Doodle</strong>.</>,
        },
        whenForm: {
            title: "Crear Nueva Encuesta de Fecha",
            description: "Crea fácilmente una encuesta online para elegir la fecha de tu próximo evento, reunión o encuentro. Averigua la disponibilidad de los participantes en pocos clics.",
        },

        teamBoard: {
            title: "Organización de Equipos y Grupos Online",
            description: "Crea un equipo para tu deporte, club o grupo de amigos. Planifica eventos fácilmente, sigue la asistencia y mantén todo organizado en un solo lugar.",
            text: <>¿Necesitas organizar <strong>entrenamientos</strong> de un equipo deportivo, <strong>ensayos</strong> de teatro o simplemente <strong>eventos regulares</strong> para un grupo de amigos? Crea tu <strong>propio equipo</strong>, planifica eventos y <strong>sigue la asistencia</strong> de todos los miembros en un solo lugar. <strong>Simple, claro y eficaz.</strong></>,
        },
        team: {
            getTitle: (title?: string) => title ? `${title} | Organización de Equipo` : "Organización de Grupos y Equipos",
            getDescription: (title?: string, description?: string) => description
                ? `${description} | Equipo ${title} en Titimeme.com.`
                : `Resumen del equipo ${title}. Planifica eventos y sigue la asistencia en Titimeme.com.`
        },
    },
};

export const dictionary = {
    cs: csDict,
    en: enDict,
    es: esDict,
};

export const urlKeys = Object.keys(csDict).filter(key => key.startsWith("url")) as (keyof Dictionary)[];